import { z } from "zod";
import { supabase, requireUserId } from "./sb-helpers";
import { resolveSampleMediaUrl } from "@/lib/onboarding/sampleTrips";

const RegisterSchema = z.object({
  tripId: z.string().uuid(),
  mimeType: z.string().min(1).max(128),
  sizeBytes: z.number().int().nonnegative(),
  originalFilename: z.string().max(512).optional(),
  capturedAt: z.string().datetime().nullable().optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  altitudeMeters: z.number().nullable().optional(),
  headingDegrees: z.number().nullable().optional(),
  width: z.number().int().nullable().optional(),
  height: z.number().int().nullable().optional(),
  durationMs: z.number().int().nullable().optional(),
  deviceMake: z.string().max(128).nullable().optional(),
  deviceModel: z.string().max(128).nullable().optional(),
  deviceOrientation: z.string().max(64).nullable().optional(),
  exifJson: z.record(z.string(), z.any()).nullable().optional(),
  sha256: z.string().max(128).nullable().optional(),
  /** SHA-256 hex of the file bytes — used for per-trip duplicate detection. */
  contentSha256: z.string().max(128).nullable().optional(),
  /** PHAsset.localIdentifier on iOS — enables idempotent re-scans. */
  localAssetIdentifier: z.string().max(256).nullable().optional(),
  /** Local pending-upload registry key (durable retry). Optional. */
  localUploadId: z.string().max(64).nullable().optional(),
  // --- Photo Direction / Camera Position Metadata v1 (additive; all nullable) ---
  positionMetadata: z.record(z.string(), z.any()).nullable().optional(),
  captureLat: z.number().nullable().optional(),
  captureLng: z.number().nullable().optional(),
  captureAccuracyM: z.number().nullable().optional(),
  captureHeadingDeg: z.number().nullable().optional(),
  captureSpeedMps: z.number().nullable().optional(),
  captureSource: z.string().max(32).nullable().optional(),
  metadataSource: z.string().max(32).nullable().optional(),
  metadataStatus: z.string().max(32).nullable().optional(),
  metadataConfidence: z.number().min(0).max(1).nullable().optional(),
});

export type RegisterMediaUploadResult =
  | { alreadyUploaded: false; mediaId: string; path: string; uploadUrl: string; token: string }
  | { alreadyUploaded: true; mediaId: string };

/**
 * Register a media row + return a signed upload URL.
 *
 * Idempotency: if `localAssetIdentifier` is set and a row already exists for
 * `(user_id, local_asset_identifier)` (enforced by `uq_media_assets_user_local_asset`),
 * returns `{ alreadyUploaded: true, mediaId }` instead of throwing. Caller
 * should skip the upload step and mark the local item done.
 */
export async function registerMediaUpload(input: {
  data: z.infer<typeof RegisterSchema>;
}): Promise<RegisterMediaUploadResult> {
  const data = RegisterSchema.parse(input.data);
  const userId = await requireUserId();

  // Idempotency pre-check (cheap, avoids the insert→23505 round-trip on every duplicate).
  if (data.localAssetIdentifier) {
    const { data: existing, error: lookupErr } = await supabase
      .from("media_assets")
      .select("id, upload_status")
      .eq("user_id", userId)
      .eq("local_asset_identifier", data.localAssetIdentifier)
      .maybeSingle();
    if (lookupErr) throw new Error(lookupErr.message);
    if (existing) {
      return { alreadyUploaded: true, mediaId: existing.id };
    }
  }

  const ext =
    data.originalFilename?.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "bin";
  const id = crypto.randomUUID();
  const path = `${data.tripId}/${userId}/${id}.${ext}`;
  const mediaType: "photo" | "video" = data.mimeType.startsWith("video") ? "video" : "photo";

  const mergedExif: Record<string, any> | null =
    data.exifJson || data.sha256
      ? { ...(data.exifJson ?? {}), ...(data.sha256 ? { sha256: data.sha256 } : {}) }
      : null;

  const { error: insErr } = await supabase.from("media_assets").insert({
    id,
    user_id: userId,
    trip_id: data.tripId,
    media_type: mediaType,
    file_size_bytes: data.sizeBytes,
    original_filename: data.originalFilename ?? null,
    captured_at: data.capturedAt ?? null,
    latitude: data.lat ?? null,
    longitude: data.lng ?? null,
    altitude_meters: data.altitudeMeters ?? null,
    heading_degrees: data.headingDegrees ?? null,
    width: data.width ?? null,
    height: data.height ?? null,
    duration_seconds: data.durationMs != null ? data.durationMs / 1000 : null,
    camera_make: data.deviceMake ?? null,
    camera_model: data.deviceModel ?? null,
    device_orientation: data.deviceOrientation ?? null,
    exif_json: mergedExif,
    storage_path: path,
    upload_status: "uploading",
    local_asset_identifier: data.localAssetIdentifier ?? null,
    local_upload_id: data.localUploadId ?? null,
    content_sha256: data.contentSha256 ?? data.sha256 ?? null,
    // Photo Direction / Camera Position Metadata v1 (additive)
    position_metadata:    data.positionMetadata    ?? null,
    capture_lat:          data.captureLat          ?? null,
    capture_lng:          data.captureLng          ?? null,
    capture_accuracy_m:   data.captureAccuracyM    ?? null,
    capture_heading_deg:  data.captureHeadingDeg   ?? null,
    capture_speed_mps:    data.captureSpeedMps     ?? null,
    capture_source:       data.captureSource       ?? null,
    metadata_source:      data.metadataSource      ?? null,
    metadata_status:      data.metadataStatus      ?? null,
    metadata_confidence:  data.metadataConfidence  ?? null,
  });
  if (insErr) {
    // 23505 = unique_violation — race with another concurrent scan/upload of the same PHAsset.
    // Treat as "already uploaded" and look up the winning row.
    if (data.localAssetIdentifier && (insErr as any).code === "23505") {
      const { data: existing } = await supabase
        .from("media_assets")
        .select("id")
        .eq("user_id", userId)
        .eq("local_asset_identifier", data.localAssetIdentifier)
        .maybeSingle();
      if (existing) return { alreadyUploaded: true, mediaId: existing.id };
    }
    throw new Error(insErr.message);
  }

  const { data: signed, error: sigErr } = await supabase.storage
    .from("trip-media")
    .createSignedUploadUrl(path);
  if (sigErr) throw new Error(sigErr.message);

  return { alreadyUploaded: false, mediaId: id, path, uploadUrl: signed.signedUrl, token: signed.token };
}

const MarkSchema = z.object({
  mediaId: z.string().uuid(),
  status: z.enum(["uploaded", "failed"]),
  errorMsg: z.string().max(512).optional(),
});

export async function markMediaUploaded(input: { data: z.infer<typeof MarkSchema> }) {
  const data = MarkSchema.parse(input.data);
  const userId = await requireUserId();
  const { error } = await supabase
    .from("media_assets")
    .update({
      upload_status: data.status,
      uploaded_at: data.status === "uploaded" ? new Date().toISOString() : null,
    })
    .eq("id", data.mediaId)
    .eq("user_id", userId);
  if (error) throw new Error(error.message);

  // Fire-and-forget AI analysis. Photos only; the edge function is idempotent
  // and skips videos / already-analyzed rows. Must never block upload success.
  if (data.status === "uploaded") {
    try {
      void supabase.functions
        .invoke("analyze-trip-media-image", { body: { mediaId: data.mediaId } })
        .catch((e) => console.warn("[ai] invoke failed", e));
    } catch (e) {
      console.warn("[ai] invoke threw", e);
    }
  }

  return { ok: true };
}

const ReanalyzeSchema = z.object({ mediaId: z.string().uuid() });

/** Admin-triggered re-run. Defense-in-depth: gate by user_roles here AND
 *  in the edge function so a non-admin can't bypass the UI by calling the
 *  endpoint directly. */
export async function reanalyzeMedia(input: { data: z.infer<typeof ReanalyzeSchema> }) {
  const data = ReanalyzeSchema.parse(input.data);
  const userId = await requireUserId();
  const { data: roles } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  const isAdmin = (roles ?? []).some((r: any) => r.role === "admin");
  if (!isAdmin) throw new Error("Forbidden");
  const { data: res, error } = await supabase.functions.invoke("analyze-trip-media-image", {
    body: { mediaId: data.mediaId, force: true, source: "reanalyze" },
  });
  if (error) throw new Error(error.message);
  return res ?? { ok: true };
}

const RecentAiSchema = z.object({
  tripId: z.string().uuid(),
  limit: z.number().int().min(1).max(100).optional(),
});

/**
 * Admin-only debug view: latest media rows for a trip with AI fields.
 * Raw storage paths and signed URLs are intentionally NOT returned —
 * only `has_storage` / `has_thumb` booleans.
 */
export async function getRecentTripMediaForAi(input: {
  data: z.infer<typeof RecentAiSchema>;
}) {
  const data = RecentAiSchema.parse(input.data);
  const userId = await requireUserId();

  // Defense-in-depth admin gate (AdminGate already redirects in UI).
  const { data: roles } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  const isAdmin = (roles ?? []).some((r: any) => r.role === "admin");
  if (!isAdmin) throw new Error("Forbidden");

  const limit = data.limit ?? 25;
  const { data: rows, error } = await supabase
    .from("media_assets")
    .select(
      "id, user_id, media_type, upload_status, created_at, uploaded_at, storage_path, thumbnail_storage_path, latitude, longitude, capture_lat, capture_lng, capture_source, ai_status, ai_error, ai_processed_at, ai_caption, ai_scene_summary, ai_tags, ai_category, ai_activity, ai_quality, ai_is_highlight, ai_travel_moment, ai_story_value, ai_location_clues, ai_visual_priority, ai_quality_score, ai_privacy_flags, ai_contains_people, ai_contains_text, ai_analysis_level",
    )
    .eq("trip_id", data.tripId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);

  // Look up uploader profiles (display_name) — best-effort.
  const uploaderIds = Array.from(new Set((rows ?? []).map((r: any) => r.user_id).filter(Boolean)));
  let profileById = new Map<string, any>();
  if (uploaderIds.length) {
    try {
      const { data: profiles } = await (supabase.rpc as any)("get_profiles_basic", { _ids: uploaderIds });
      profileById = new Map((profiles ?? []).map((p: any) => [p.id, p]));
    } catch {
      profileById = new Map();
    }
  }

  // Strip raw paths before returning — only expose presence booleans + signed thumbnail.
  const items = await Promise.all(
    (rows ?? []).map(async (r: any) => {
      // Best-effort thumbnail signing. Never throws, never blocks the row.
      let thumb_url: string | null = null;
      const thumbPath: string | null = r.thumbnail_storage_path ?? null;
      if (thumbPath) {
        const sample = resolveSampleMediaUrl(thumbPath);
        if (sample) {
          thumb_url = sample;
        } else if (/^https?:\/\//i.test(thumbPath)) {
          thumb_url = thumbPath;
        } else {
          try {
            const { data: signed } = await supabase.storage
              .from("trip-media")
              .createSignedUrl(thumbPath, 60 * 60);
            thumb_url = signed?.signedUrl ?? null;
          } catch {
            thumb_url = null;
          }
        }
      }

      const prof = profileById.get(r.user_id) as any;
      const uploaderName: string | null = prof?.display_name ?? null;

      const tagsArr: string[] = Array.isArray(r.ai_tags) ? r.ai_tags : [];
      const tagsHaveSeed = tagsArr.some((t) => typeof t === "string" && t.toLowerCase() === "seed");
      const nameLooksSeed = !!uploaderName && /\(seed\)/i.test(uploaderName);
      const is_seed = r.capture_source === "seed" || tagsHaveSeed || nameLooksSeed;

      const has_gps =
        r.latitude != null || r.longitude != null || r.capture_lat != null || r.capture_lng != null;

      return {
        id: r.id as string,
        media_type: r.media_type as "photo" | "video",
        upload_status: r.upload_status as string,
        created_at: r.created_at as string,
        uploaded_at: (r.uploaded_at as string | null) ?? null,
        has_storage: !!r.storage_path,
        has_thumb: !!r.thumbnail_storage_path,
        thumb_url,
        uploader_id: (r.user_id as string) ?? "",
        uploader_name: uploaderName,
        has_gps,
        is_seed,
        ai_status: (r.ai_status as string | null) ?? null,
        ai_error: (r.ai_error as string | null) ?? null,
        ai_processed_at: (r.ai_processed_at as string | null) ?? null,
        ai_caption: (r.ai_caption as string | null) ?? null,
        ai_scene_summary: (r.ai_scene_summary as string | null) ?? null,
        ai_tags: (r.ai_tags as string[] | null) ?? null,
        ai_category: (r.ai_category as string | null) ?? null,
        ai_activity: (r.ai_activity as string | null) ?? null,
        ai_quality: (r.ai_quality as string | null) ?? null,
        ai_is_highlight: (r.ai_is_highlight as boolean | null) ?? null,
        ai_travel_moment: (r.ai_travel_moment as string | null) ?? null,
        ai_story_value: (r.ai_story_value as string | null) ?? null,
        ai_location_clues: (r.ai_location_clues as string[] | null) ?? null,
        ai_visual_priority: (r.ai_visual_priority as number | null) ?? null,
        ai_quality_score: (r.ai_quality_score as number | null) ?? null,
        ai_privacy_flags: (r.ai_privacy_flags as string[] | null) ?? null,
        ai_contains_people: (r.ai_contains_people as boolean | null) ?? null,
        ai_contains_text: (r.ai_contains_text as boolean | null) ?? null,
        ai_analysis_level: (r.ai_analysis_level as string | null) ?? null,
      };
    }),
  );
  return { items };
}

const RetrySchema = z.object({ mediaId: z.string().uuid() });

/**
 * Legacy retry — flips upload_status back to "queued" without touching bytes.
 * Kept for backward compat but the Queue UI no longer calls this; durable
 * retry uses `prepareRetryUpload` to re-issue a signed URL and re-PUT the
 * locally persisted blob.
 */
export async function retryMediaUpload(input: { data: z.infer<typeof RetrySchema> }) {
  const data = RetrySchema.parse(input.data);
  const userId = await requireUserId();
  const { error } = await supabase
    .from("media_assets")
    .update({ upload_status: "queued" })
    .eq("id", data.mediaId)
    .eq("user_id", userId);
  if (error) throw new Error(error.message);
  return { ok: true };
}

const PrepareRetrySchema = z.object({ mediaId: z.string().uuid() });

export type PrepareRetryResult = {
  mediaId: string;
  path: string;
  token: string;
  localUploadId: string | null;
  originalFilename: string | null;
  mimeType: string;
};

/**
 * Re-issue a signed upload URL for an existing media_assets row so the
 * client can re-PUT bytes it still has locally. Flips status to "uploading".
 * Scoped to the row's owner via .eq("user_id", userId).
 */
export async function prepareRetryUpload(input: {
  data: z.infer<typeof PrepareRetrySchema>;
}): Promise<PrepareRetryResult> {
  const data = PrepareRetrySchema.parse(input.data);
  const userId = await requireUserId();
  const { data: row, error: rowErr } = await supabase
    .from("media_assets")
    .select("id, storage_path, local_upload_id, original_filename, media_type")
    .eq("id", data.mediaId)
    .eq("user_id", userId)
    .maybeSingle();
  if (rowErr) throw new Error(rowErr.message);
  if (!row || !row.storage_path) throw new Error("Upload row not found");

  const { data: signed, error: sigErr } = await supabase.storage
    .from("trip-media")
    .createSignedUploadUrl(row.storage_path);
  if (sigErr) throw new Error(sigErr.message);

  await supabase
    .from("media_assets")
    .update({ upload_status: "uploading" })
    .eq("id", data.mediaId)
    .eq("user_id", userId);

  const mime = row.media_type === "video" ? "video/mp4" : "image/jpeg";

  return {
    mediaId: row.id as string,
    path: row.storage_path as string,
    token: signed.token,
    localUploadId: (row as any).local_upload_id ?? null,
    originalFilename: row.original_filename ?? null,
    mimeType: mime,
  };
}


/**
 * Discard a media_assets row that's stuck in `queued`/`uploading` (or
 * `failed`) and the user wants gone — clears the "pending" counter when the
 * original bytes are no longer recoverable. Server-side guarded so we never
 * cancel a row that started uploading less than 10 minutes ago.
 */
const DiscardStuckSchema = z.object({ mediaId: z.string().uuid() });
export async function discardStuckUpload(input: {
  data: z.infer<typeof DiscardStuckSchema>;
}): Promise<{ ok: true } | { ok: false; reason: string }> {
  const data = DiscardStuckSchema.parse(input.data);
  const userId = await requireUserId();

  const { data: row, error: rowErr } = await supabase
    .from("media_assets")
    .select("id, upload_status, created_at")
    .eq("id", data.mediaId)
    .eq("user_id", userId)
    .maybeSingle();
  if (rowErr) throw new Error(rowErr.message);
  if (!row) return { ok: false, reason: "not-found" };

  const status = row.upload_status as string | null;
  if (status === "uploaded") return { ok: false, reason: "already-uploaded" };
  const ageMs = Date.now() - new Date(row.created_at as string).getTime();
  if ((status === "queued" || status === "uploading") && ageMs < 10 * 60 * 1000) {
    return { ok: false, reason: "too-recent" };
  }

  const { error: delErr } = await supabase
    .from("media_assets")
    .delete()
    .eq("id", data.mediaId)
    .eq("user_id", userId);
  if (delErr) throw new Error(delErr.message);
  return { ok: true };
}

/**
 * Best-effort: flip a stuck `uploading` row to `failed` so it stops blocking
 * the pending counter. Caller has already confirmed the local blob is gone
 * (so retry isn't possible) — flipping to failed lets the existing failed-row
 * UI surface "Re-pick required" or Discard.
 */
const MarkFailedSchema = z.object({ mediaId: z.string().uuid() });
export async function markStuckUploadFailed(input: {
  data: z.infer<typeof MarkFailedSchema>;
}): Promise<{ ok: boolean }> {
  const data = MarkFailedSchema.parse(input.data);
  const userId = await requireUserId();
  const { error } = await supabase
    .from("media_assets")
    .update({ upload_status: "failed" })
    .eq("id", data.mediaId)
    .eq("user_id", userId)
    .eq("upload_status", "uploading");
  if (error) throw new Error(error.message);
  return { ok: true };
}


const TripIdSchema = z.object({ tripId: z.string().uuid() });

export async function getUploadRules(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  const userId = await requireUserId();
  const { data: row } = await supabase
    .from("permissions_status")
    .select("wifi_only_pref, upload_originals_pref, compressed_previews_first_pref, upload_mode, min_battery_pct, require_charging_under_min")
    .eq("user_id", userId)
    .eq("trip_id", data.tripId)
    .maybeSingle();
  const r: any = row ?? {};
  return {
    wifiOnly: r.wifi_only_pref ?? true,
    uploadOriginals: r.upload_originals_pref ?? true,
    compressedPreviewsFirst: r.compressed_previews_first_pref ?? false,
    uploadMode: (r.upload_mode ?? "auto") as "auto" | "manual",
    minBatteryPct: r.min_battery_pct ?? 20,
    requireChargingUnderMin: r.require_charging_under_min ?? true,
  };
}

const SaveRulesSchema = z.object({
  tripId: z.string().uuid(),
  wifiOnly: z.boolean(),
  uploadOriginals: z.boolean(),
  compressedPreviewsFirst: z.boolean(),
  uploadMode: z.enum(["auto", "manual"]).optional(),
  minBatteryPct: z.number().int().min(0).max(100).optional(),
  requireChargingUnderMin: z.boolean().optional(),
});

export async function saveUploadRules(input: { data: z.infer<typeof SaveRulesSchema> }) {
  const data = SaveRulesSchema.parse(input.data);
  const userId = await requireUserId();
  const patch: Record<string, any> = {
    user_id: userId,
    trip_id: data.tripId,
    wifi_only_pref: data.wifiOnly,
    upload_originals_pref: data.uploadOriginals,
    compressed_previews_first_pref: data.compressedPreviewsFirst,
    updated_at: new Date().toISOString(),
  };
  if (data.uploadMode !== undefined) patch.upload_mode = data.uploadMode;
  if (data.minBatteryPct !== undefined) patch.min_battery_pct = data.minBatteryPct;
  if (data.requireChargingUnderMin !== undefined) patch.require_charging_under_min = data.requireChargingUnderMin;
  const { error } = await supabase.from("permissions_status").upsert(patch, { onConflict: "user_id,trip_id" });
  if (error) throw new Error(error.message);
  return { ok: true };
}


export async function getMyMedia(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  const userId = await requireUserId();
  const { data: items, error } = await supabase
    .from("media_assets")
    .select("*")
    .eq("trip_id", data.tripId)
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw new Error(error.message);

  const withUrls = await Promise.all(
    (items ?? []).map(async (it: any) => {
      if (it.upload_status !== "uploaded" || !it.storage_path) return { ...it, url: null };
      const sample = resolveSampleMediaUrl(it.storage_path);
      if (sample) return { ...it, url: sample };
      if (/^https?:\/\//i.test(it.storage_path)) return { ...it, url: it.storage_path };
      const { data: signed } = await supabase.storage
        .from("trip-media")
        .createSignedUrl(it.storage_path, 60 * 60);
      return { ...it, url: signed?.signedUrl ?? null };
    }),
  );
  return { items: withUrls };
}

const PingSchema = z.object({
  tripId: z.string().uuid(),
  lat: z.number(),
  lng: z.number(),
  accuracy: z.number().nullable().optional(),
  altitude: z.number().nullable().optional(),
  speed: z.number().nullable().optional(),
  heading: z.number().nullable().optional(),
  capturedAt: z.string().datetime().optional(),
  source: z
    .enum(["foreground", "background", "manual", "manual_gps", "native_ios_background"])
    .default("foreground"),
  // Optional deterministic idempotency key. When present, DB enforces uniqueness
  // via `ux_location_points_client_point_key`, so duplicate inserts caused by
  // racing drains are dropped server-side. Keyless rows behave as before.
  clientPointKey: z.string().min(1).max(120).nullable().optional(),
});

function pingToRow(data: z.infer<typeof PingSchema>, userId: string) {
  return {
    user_id: userId,
    trip_id: data.tripId,
    latitude: data.lat,
    longitude: data.lng,
    horizontal_accuracy_meters: data.accuracy ?? null,
    altitude_meters: data.altitude ?? null,
    speed_mps: data.speed ?? null,
    heading_degrees: data.heading ?? null,
    captured_at: data.capturedAt ?? new Date().toISOString(),
    source: data.source,
    client_point_key: data.clientPointKey ?? null,
  };
}

export async function recordLocationPing(input: { data: z.input<typeof PingSchema> }) {
  const data = PingSchema.parse(input.data);
  const userId = await requireUserId();
  const row = pingToRow(data, userId);
  // Use upsert+ignoreDuplicates when a key is present so PostgREST hits
  // ux_location_points_client_point_key and silently no-ops on conflict.
  // Without a key, fall through to plain insert (matches previous behavior).
  if (row.client_point_key) {
    const { error } = await supabase
      .from("location_points")
      .upsert(row, { onConflict: "client_point_key", ignoreDuplicates: true });
    if (error) throw new Error(error.message);
  } else {
    const { error } = await supabase.from("location_points").insert(row);
    if (error) throw new Error(error.message);
  }
  return { ok: true };
}

const PingBatchSchema = z.object({
  pings: z.array(PingSchema).min(1).max(500),
});

export async function recordLocationPingsBatch(input: { data: z.input<typeof PingBatchSchema> }) {
  const data = PingBatchSchema.parse(input.data);
  const userId = await requireUserId();
  const rows = data.pings.map((p) => pingToRow(p, userId));
  const keyed = rows.filter((r) => r.client_point_key != null);
  const keyless = rows.filter((r) => r.client_point_key == null);
  if (keyed.length > 0) {
    const { error } = await supabase
      .from("location_points")
      .upsert(keyed, { onConflict: "client_point_key", ignoreDuplicates: true });
    if (error) throw new Error(error.message);
  }
  if (keyless.length > 0) {
    const { error } = await supabase.from("location_points").insert(keyless);
    if (error) throw new Error(error.message);
  }
  return { ok: true, inserted: rows.length };
}

export async function getMyPingStats(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  const userId = await requireUserId();
  const { count } = await supabase
    .from("location_points")
    .select("id", { count: "exact", head: true })
    .eq("trip_id", data.tripId)
    .eq("user_id", userId);

  const { data: last } = await supabase
    .from("location_points")
    .select("captured_at, latitude, longitude")
    .eq("trip_id", data.tripId)
    .eq("user_id", userId)
    .order("captured_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  return { count: count ?? 0, last };
}

const GetTrackSchema = z.object({
  tripId: z.string().uuid(),
  since: z.string().datetime().optional(),
});

export async function getMyTrack(input: { data: z.infer<typeof GetTrackSchema> }) {
  const data = GetTrackSchema.parse(input.data);
  const userId = await requireUserId();
  let q = supabase
    .from("location_points")
    .select("latitude, longitude, captured_at")
    .eq("trip_id", data.tripId)
    .eq("user_id", userId)
    .order("captured_at", { ascending: true })
    .limit(2000);
  if (data.since) q = q.gte("captured_at", data.since);
  const { data: rows, error } = await q;
  if (error) throw new Error(error.message);
  const points = (rows ?? []).map((r: any) => ({
    lat: Number(r.latitude),
    lng: Number(r.longitude),
    capturedAt: r.captured_at as string,
  }));
  return { points };
}

const GetTripTracksSchema = z.object({
  tripId: z.string().uuid(),
  since: z.string().datetime().optional(),
  userIds: z.array(z.string().uuid()).max(50).optional(),
});

export async function getTripTracks(input: { data: z.infer<typeof GetTripTracksSchema> }) {
  const data = GetTripTracksSchema.parse(input.data);
  await requireUserId();

  // Single authoritative backend call. Returns ALL pings for the trip in
  // one round-trip — no PostgREST 1000-row pagination, no per-user loop,
  // no client-side outlier filtering or simplification.
  const { data: payload, error } = await (supabase.rpc as any)(
    "get_trip_tracks_v1",
    { _trip_id: data.tripId },
  );
  if (error) {
    console.error("[getTripTracks] rpc get_trip_tracks_v1 failed", error.message);
    throw new Error(error.message);
  }

  const rawCountByUser: Record<string, number> = {};
  const tracksByUser: Record<string, { lat: number; lng: number; capturedAt: string }[]> = {};
  const fetchedCountByUser: Record<string, number> = {};
  const outlierCountByUser: Record<string, number> = {};
  const simplifiedCountByUser: Record<string, number> = {};

  const rawObj = (payload?.rawCountByUser ?? {}) as Record<string, number>;
  for (const [uid, n] of Object.entries(rawObj)) {
    rawCountByUser[uid] = Number(n) || 0;
  }

  const tracksObj = (payload?.tracksByUser ?? {}) as Record<
    string,
    Array<{ lat: number; lng: number; capturedAt: string }>
  >;
  let optionalUserIds = data.userIds && data.userIds.length > 0 ? new Set(data.userIds) : null;
  for (const [uid, pts] of Object.entries(tracksObj)) {
    if (optionalUserIds && !optionalUserIds.has(uid)) continue;
    let arr = (pts ?? []).map((p: any) => ({
      lat: Number(p.lat),
      lng: Number(p.lng),
      capturedAt: String(p.capturedAt),
    }));
    if (data.since) {
      const cutoff = new Date(data.since).getTime();
      arr = arr.filter((p) => new Date(p.capturedAt).getTime() >= cutoff);
    }
    tracksByUser[uid] = arr;
    fetchedCountByUser[uid] = arr.length;
    simplifiedCountByUser[uid] = arr.length;
    outlierCountByUser[uid] = 0;
  }

  const serverTotal = Number(payload?.serverTotal ?? 0) || 0;
  const finalTotal = Object.values(tracksByUser).reduce((s, a) => s + a.length, 0);
  console.log("[getTripTracks] rpc result", {
    tripId: data.tripId,
    serverTotal,
    finalTotal,
    rawCountByUser,
  });

  return {
    tracksByUser,
    truncated: false,
    serverTotal,
    rawCountByUser,
    fetchedCountByUser,
    outlierCountByUser,
    simplifiedCountByUser,
  };
}


const PermSchema = z.object({
  tripId: z.string().uuid(),
  locationGranted: z.boolean(),
  photosGranted: z.boolean(),
  wifiOnlyPref: z.boolean(),
  notificationsGranted: z.boolean(),
});

export async function savePermissions(input: { data: z.infer<typeof PermSchema> }) {
  const data = PermSchema.parse(input.data);
  const userId = await requireUserId();
  const { error } = await supabase.from("permissions_status").upsert(
    {
      user_id: userId,
      trip_id: data.tripId,
      location_granted: data.locationGranted,
      photos_granted: data.photosGranted,
      wifi_only_pref: data.wifiOnlyPref,
      notifications_granted: data.notificationsGranted,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id,trip_id" },
  );
  if (error) throw new Error(error.message);
  return { ok: true };
}

export async function getPermissions(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  const userId = await requireUserId();
  const { data: row } = await supabase
    .from("permissions_status")
    .select("*")
    .eq("user_id", userId)
    .eq("trip_id", data.tripId)
    .maybeSingle();
  return { permissions: row };
}

const DiagnosticsSchema = z.object({
  tripId: z.string().uuid(),
  pluginInventory: z.array(z.object({
    name: z.string().max(64),
    state: z.string().max(64),
    probedMethod: z.string().max(120).nullable().optional(),
  })).max(32),
  appVersion: z.string().max(32).nullable().optional(),
  appBuild: z.string().max(32).nullable().optional(),
  otaBuildId: z.string().max(64).nullable().optional(),
  locationAuthLevel: z.string().max(32).nullable().optional(),
  platform: z.string().max(32).nullable().optional(),
  deviceModel: z.string().max(64).nullable().optional(),
  osVersion: z.string().max(32).nullable().optional(),
  batteryLevel: z.number().int().min(0).max(100).nullable().optional(),
  batteryState: z.string().max(16).nullable().optional(),
  lowPowerMode: z.boolean().nullable().optional(),
  networkType: z.string().max(16).nullable().optional(),
  pushRegistered: z.boolean().nullable().optional(),
  liveActivityState: z.string().max(32).nullable().optional(),
  healthkitState: z.string().max(32).nullable().optional(),
  shellMode: z.string().max(16).nullable().optional(),
  lastShellError: z.string().max(512).nullable().optional(),
  nativeQueueCount: z.number().int().min(0).nullable().optional(),
  lastNativeLocationAt: z.string().nullable().optional(),
});

export async function upsertMyDiagnostics(input: { data: z.infer<typeof DiagnosticsSchema> }) {
  const data = DiagnosticsSchema.parse(input.data);
  await requireUserId();
  const { error } = await supabase.rpc("upsert_my_diagnostics", {
    _trip_id: data.tripId,
    _plugin_inventory: data.pluginInventory,
    _app_version: data.appVersion ?? undefined,
    _app_build: data.appBuild ?? undefined,
    _ota_build_id: data.otaBuildId ?? undefined,
    _location_auth_level: data.locationAuthLevel ?? undefined,
    _platform: data.platform ?? undefined,
    _device_model: data.deviceModel ?? undefined,
    _os_version: data.osVersion ?? undefined,
    _battery_level: data.batteryLevel ?? undefined,
    _battery_state: data.batteryState ?? undefined,
    _low_power_mode: data.lowPowerMode ?? undefined,
    _network_type: data.networkType ?? undefined,
    _push_registered: data.pushRegistered ?? undefined,
    _live_activity_state: data.liveActivityState ?? undefined,
    _healthkit_state: data.healthkitState ?? undefined,
    _shell_mode: data.shellMode ?? undefined,
    _last_shell_error: data.lastShellError ?? undefined,
    _native_queue_count: data.nativeQueueCount ?? undefined,
    _last_native_location_at: data.lastNativeLocationAt ?? undefined,
  } as any);
  if (error) throw new Error(error.message);
  return { ok: true };
}


/** Group library: every uploaded asset for the trip, plus uploader names + isAdmin flag. */
export async function getTripLibrary(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  const userId = await requireUserId();

  // PostgREST hard-caps responses at 1000 rows regardless of .limit().
  // Page through with .range() until exhausted or safety cap hit.
  const PAGE = 1000;
  const MAX_ROWS = 10000;
  const items: any[] = [];
  let libraryTruncated = false;
  for (let from = 0; from < MAX_ROWS; from += PAGE) {
    const to = Math.min(from + PAGE - 1, MAX_ROWS - 1);
    const { data: chunk, error } = await supabase
      .from("media_assets")
      .select("*")
      .eq("trip_id", data.tripId)
      .order("captured_at", { ascending: false, nullsFirst: false })
      .order("id", { ascending: false })
      .range(from, to);
    if (error) {
      console.error("[getTripLibrary] chunk failed", { from, to, error: error.message });
      throw new Error(error.message);
    }
    const rows = chunk ?? [];
    console.log("[getTripLibrary] chunk", { from, to, returned: rows.length });
    items.push(...rows);
    if (rows.length < PAGE) break;
    if (from + PAGE >= MAX_ROWS) libraryTruncated = true;
  }

  const uploaderIds = Array.from(new Set((items ?? []).map((i: any) => i.user_id)));
  const { data: profiles } = uploaderIds.length
    ? await (supabase.rpc as any)("get_profiles_basic", { _ids: uploaderIds })
    : { data: [] as any[] };
  const byId = new Map((profiles ?? []).map((p: any) => [p.id, p]));

  const withMeta = await Promise.all(
    (items ?? []).map(async (it: any) => {
      let url: string | null = null;
      if (it.upload_status === "uploaded" && it.storage_path) {
        // Resolve "sample://..." sample-trip paths, raw http(s) seed URLs,
        // otherwise sign the bucket key.
        const sample = resolveSampleMediaUrl(it.storage_path);
        if (sample) {
          url = sample;
        } else if (/^https?:\/\//i.test(it.storage_path)) {
          url = it.storage_path;
        } else {
          const { data: signed } = await supabase.storage
            .from("trip-media")
            .createSignedUrl(it.storage_path, 60 * 60);
          url = signed?.signedUrl ?? null;
        }
      }
      const p = byId.get(it.user_id) as any;
      return {
        ...it,
        url,
        uploader_name: p?.display_name ?? "Traveler",
        uploader_avatar: p?.avatar_url ?? null,
      };
    }),
  );

  const { data: tm } = await supabase
    .from("trip_members")
    .select("role")
    .eq("trip_id", data.tripId)
    .eq("user_id", userId)
    .maybeSingle();
  const { data: roles } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  const isAdmin =
    (tm as any)?.role === "admin" || (roles ?? []).some((r: any) => r.role === "admin");

  return { items: withMeta, isAdmin, truncated: libraryTruncated };
}

const FlagSchema = z.object({
  mediaId: z.string().uuid(),
  isFavorite: z.boolean().optional(),
  excludedFromMontage: z.boolean().optional(),
  needsReview: z.boolean().optional(),
});

export async function setMediaFlags(input: { data: z.infer<typeof FlagSchema> }) {
  const data = FlagSchema.parse(input.data);
  await requireUserId();

  const { error } = await supabase.rpc("set_media_flags", {
    _media_id: data.mediaId,
    _is_favorite: data.isFavorite,
    _excluded_from_montage: data.excludedFromMontage,
    _needs_review: data.needsReview,
  });

  if (error) throw new Error(error.message);
  return { ok: true };
}

const FamilySharedSchema = z.object({
  mediaId: z.string().uuid(),
  familyShared: z.boolean(),
});

/**
 * Toggle the per-photo "share with family" flag. Owner-only — RLS on
 * media_assets restricts updates to user_id = auth.uid().
 * Read by the family-follow-feed edge function when photo_mode='selected'.
 */
export async function setMyMediaFamilyShared(input: {
  data: z.infer<typeof FamilySharedSchema>;
}) {
  const { mediaId, familyShared } = FamilySharedSchema.parse(input.data);
  const uid = await requireUserId();
  const { error } = await supabase
    .from("media_assets")
    .update({ family_shared: familyShared })
    .eq("id", mediaId)
    .eq("user_id", uid);
  if (error) throw new Error(error.message);
  return { ok: true };
}


